import {Badge} from '@mantine/core'

import {OutOfStateBadge} from 'src/ui/out-of-state-badge'

import {PartialPickupIcon} from '../work/partial-pickup'
import type {BoardLaneJob} from './board-lanes'
import type {BoardLane} from './board-lanes'
import classes from './dispatch.module.css'
import type {OperationalSignalTier} from './radio-status'
import {resolveRadioStatus} from './radio-status'
import type {TerminalScheduleLabel} from './schedule-language'
import {terminalScheduleLabelFromContext} from './schedule-language'
import {getScheduleUrgency, scheduleUrgencyClass, type UrgencyContext} from './schedule-urgency'
import {terminalIdentity} from './terminal-identity'
import type {TerminalRowEntry} from './terminal-jobs'
import type {ViewRange} from './types'
import type {ScheduleDate} from './types'
import {businessNowHour} from './work-day'

type TerminalRowProps = {
    entry: TerminalRowEntry | BoardLaneJob
    visibleDays: ScheduleDate[]
    viewRange: ViewRange
    viewDayOffset?: number
    isContinuationDay?: boolean
    highlighted?: boolean
    /** Zebra striping: shades every other row in a lane. */
    alternate?: boolean
    compact?: boolean
    nowHour?: number
    onPress: (entry: BoardLaneJob) => void
}

function accentClassForLane(lane: BoardLane): string {
    switch (lane) {
        case 'needs_action':
            return classes['accentNeedsAction']!
        case 'notify_drivers':
            return classes['accentNotifyDrivers']!
        case 'dispatched':
            return classes['accentDispatched']!
    }
}

function statusClassForSignal(tier: OperationalSignalTier): string {
    switch (tier) {
        case 'intervention':
            return classes['statusSignalIntervention']!
        case 'attention':
            return classes['statusSignalAttention']!
        case 'informational':
            return classes['statusSignalInformational']!
    }
}

function rowClassForLane(lane: BoardLane, compact: boolean, alternate: boolean): string {
    const parts = [classes['dispatchRow']!, classes['dispatchRowInteractive']!]
    if (lane === 'needs_action' || lane === 'notify_drivers' || lane === 'dispatched') {
        parts.push(classes['dispatchRowWithMeta']!)
    }
    if (alternate) {
        parts.push(classes['dispatchRowAlternate']!)
    }
    if (lane === 'notify_drivers') {
        parts.push(classes['dispatchRowNotifyDrivers']!)
    }
    if (compact) {
        parts.push(classes['dispatchRowContinuation']!)
    }
    return parts.join(' ')
}

function resolveRowContext(
    entry: TerminalRowEntry | BoardLaneJob,
    viewDayOffset?: number,
    isContinuationDay?: boolean,
): {lane: BoardLane; viewDayOffset: number; isContinuationDay: boolean} {
    if ('viewDayOffset' in entry) {
        return {
            lane: entry.lane,
            viewDayOffset: entry.viewDayOffset,
            isContinuationDay: entry.isContinuationDay,
        }
    }

    return {
        lane: entry.lane,
        viewDayOffset: viewDayOffset ?? entry.job.span?.dayOffset ?? 0,
        isContinuationDay: isContinuationDay ?? false,
    }
}

export function CloserYardTag({yardName}: {yardName: string}) {
    return (
        <Badge size="xs" variant="light" color="yellow" data-testid="closer-yard-tag">
            Closer to {yardName}
        </Badge>
    )
}

export const NO_YARD_WARNING = 'Order has no yard. Shows on every yard schedule'

/** Chip for automatic rentals that dispatch has not confirmed went out. */
export function NotConfirmedTag({count}: {count: number}) {
    return (
        <Badge
            size="xs"
            variant="light"
            color="orange"
            title="Went on rent automatically. Confirm out once the equipment has left"
            data-testid="not-confirmed-tag"
        >
            {count > 1 ? `${count} not confirmed` : 'Not confirmed'}
        </Badge>
    )
}

export function NoYardTag() {
    return (
        <Badge
            size="xs"
            variant="light"
            color="yellow"
            title={NO_YARD_WARNING}
            data-testid="no-yard-tag"
        >
            No yard yet
        </Badge>
    )
}

export function TerminalRow({
    entry,
    visibleDays,
    viewRange,
    viewDayOffset,
    isContinuationDay,
    highlighted = false,
    alternate = false,
    compact = false,
    nowHour,
    onPress,
}: TerminalRowProps) {
    const {job, display, operational, lane} = entry
    const rowContext = resolveRowContext(entry, viewDayOffset, isContinuationDay)
    const status = resolveRadioStatus(display, operational, {
        warnings: job.warnings,
        unconfirmedRentalCount: job.unconfirmedRentalCount,
    })
    const identity = terminalIdentity(display)
    const urgencyContext: UrgencyContext = {
        visibleDays,
        viewRange,
        nowHour: nowHour ?? businessNowHour(),
    }
    const schedule: TerminalScheduleLabel = terminalScheduleLabelFromContext(
        job,
        display,
        urgencyContext,
        {
            viewDayOffset: rowContext.viewDayOffset,
            isContinuationDay: rowContext.isContinuationDay,
        },
    )
    const urgency =
        'viewDayOffset' in entry
            ? getScheduleUrgency(entry, urgencyContext)
            : getScheduleUrgency(
                  {
                      ...entry,
                      viewDayOffset: rowContext.viewDayOffset,
                      isContinuationDay: rowContext.isContinuationDay,
                  } as TerminalRowEntry,
                  urgencyContext,
              )
    const scheduleClass = classes[scheduleUrgencyClass(urgency.tier) as keyof typeof classes]

    return (
        <div
            role="button"
            tabIndex={0}
            className={`${rowClassForLane(lane, compact, alternate)} ${highlighted ? classes['highlighted'] : ''}`}
            onClick={() => onPress(entry)}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onPress(entry)
                }
            }}
        >
            <div className={`${classes['accentBar']!} ${accentClassForLane(lane)}`} aria-hidden />
            <div className={classes['identityBlock']}>
                <div className={classes['identityTitle']}>
                    {identity.title}
                    {job.closerYardName ? (
                        <>
                            {' '}
                            <CloserYardTag yardName={job.closerYardName} />
                        </>
                    ) : null}
                    {job.outOfStateLabel ? (
                        <>
                            {' '}
                            <OutOfStateBadge label={job.outOfStateLabel} />
                        </>
                    ) : null}
                    {job.noYard ? (
                        <>
                            {' '}
                            <NoYardTag />
                        </>
                    ) : null}
                    {job.partialPickup ? (
                        <>
                            {' '}
                            <PartialPickupIcon partialPickup={job.partialPickup} />
                        </>
                    ) : null}
                    {job.unconfirmedRentalCount ? (
                        <>
                            {' '}
                            <NotConfirmedTag count={job.unconfirmedRentalCount} />
                        </>
                    ) : null}
                </div>
                {!compact && (
                    <div className={classes['identityMeta']}>
                        {identity.customer && (
                            <>
                                <span className={classes['identityCustomer']}>
                                    {identity.customer}
                                </span>
                                <span className={classes['identityMetaSeparator']}> · </span>
                            </>
                        )}
                        <span>Rep: {job.internalContactName ?? 'Unassigned'}</span>
                        <span className={classes['identityMetaSeparator']}> · </span>
                        <span className={classes['identityJobCode']}>{identity.jobCode}</span>
                        {identity.requirementSummary && (
                            <>
                                <span className={classes['identityMetaSeparator']}> · </span>
                                <span className={classes['identityRequirementSummary']}>
                                    {identity.requirementSummary}
                                </span>
                            </>
                        )}
                    </div>
                )}
            </div>
            <div className={classes['statusBlock']}>
                <div className={statusClassForSignal(status.tier)}>{status.line}</div>
            </div>
            <div className={classes['scheduleBlock']}>
                <div className={classes['scheduleAnchorGroup']}>
                    {schedule.status && (
                        <span className={classes['scheduleStatus']}>{schedule.status}</span>
                    )}
                    <span className={`${classes['scheduleAnchor']!} ${scheduleClass ?? ''}`}>
                        {schedule.anchor}
                    </span>
                </div>
                {schedule.duration && !compact && (
                    <div
                        className={
                            schedule.secondaryKind === 'time'
                                ? classes['scheduleTime']
                                : classes['scheduleDuration']
                        }
                    >
                        {schedule.duration}
                    </div>
                )}
            </div>
            <span className={classes['rowChevron']} aria-hidden>
                ›
            </span>
        </div>
    )
}

export function TerminalRowCompact(props: Omit<TerminalRowProps, 'compact'>) {
    return <TerminalRow {...props} compact />
}
